import assign from 'lodash.assign';

const { createHigherOrderComponent } = wp.compose;
const { Fragment, useState, useEffect } = wp.element;
const { InspectorAdvancedControls } = wp.blockEditor;
const { TextareaControl } = wp.components;
const { addFilter } = wp.hooks;
const { __ } = wp.i18n;


// Blocks that are not compatible at all with visibility controls.
const globallyRestricted = ['core/freeform', 'core/legacy-widget', 'core/widget-area'];

/**
 * Add layout control attribute to block.
 *
 * @param {object} settings Current block settings.
 * @param {string} name Name of block.
 *
 * @returns {object} Modified block settings.
 */
const addBlockLogicControlAttribute = (settings, name) => {
	// The freeform (Classic Editor) block is incompatible because it does not
	// support custom attributes.
	if (globallyRestricted.includes(settings.name)) {
		return settings;
	}

	if (typeof settings.attributes !== 'undefined') {
		const attributes = {
			blockLogic: {
				type: 'string',
				default: '',
			},
		}

		// Use Lodash's assign to gracefully handle if attributes are undefined
		settings.attributes = assign(settings.attributes, attributes);
		settings.supports = assign(settings.supports, {
			blockLogic: true,
		});
	}

	return settings;
};

addFilter('blocks.registerBlockType', 'blockLogic/attribute', addBlockLogicControlAttribute);

/**
 * Create HOC to add layout control to inspector controls of block.
 */
const withLogicControl = createHigherOrderComponent((BlockEdit) => {
	return (props) => {
		const { attributes, setAttributes, isSelected } = props;
		const { blockLogic } = attributes;

		if (blockLogic) {
			attributes.blockLogic = blockLogic;
		}

		return (
			<Fragment>
				<BlockEdit {...props} />
				{isSelected && (!globallyRestricted.includes(props.name)) &&
					<InspectorAdvancedControls>
						<TextareaControl
							rows="2"
							label={__('Block Logic', 'block-logic')}
							help={__('Add allowed and valid PHP conditional tags or PHP condition that returns true or false.', 'block-logic')}
							value={blockLogic ? blockLogic : ''}
							onChange={(newValue) =>
								setAttributes({
									blockLogic: newValue,
								})
							}
						/>
					</InspectorAdvancedControls>
				}
			</Fragment>
		);
	};
}, 'withLogicControl');

addFilter('editor.BlockEdit', 'blockLogic/control', withLogicControl);

// add a data attribute to the block wrapper "editor-block-list__block"
// this hook only fires for the BE/Admin View
const addWrapperDataAttribute = createHigherOrderComponent((BlockListBlock) => {
	return (props) => {
		const { blockLogic } = props.attributes;
		const { name } = props.block;

		let newName = name.replace(/\w+\//gm, '');
		newName = newName.charAt(0).toUpperCase() + newName.slice(1);

		let wrapperProps = props.wrapperProps;
		let customData = {};

		if (typeof blockLogic !== 'undefined' && blockLogic) {
			customData = assign(customData, {
				'data-spk-block-logic': __('Block Logic:', 'block-logic') + ' ' + blockLogic,
			});
		}

		wrapperProps = assign(wrapperProps, customData);

		// --- NEW: Add error attribute ---
		const [error, setError] = useState(null);

		useEffect(() => {
			if (!blockLogic) {
				setError(null);
				return;
			}

			// Remove only extra backslashes, preserve quotes
			let cleanedExpr = blockLogic.replace(/\\/g, '');

			const formData = new FormData();
			formData.append('action', 'block_logic_evaluate');
			formData.append('expr', cleanedExpr);
			formData.append('nonce', BlockLogicData.nonce);


			fetch(BlockLogicData.ajaxUrl, {
				method: 'POST',
				credentials: 'same-origin',
				body: formData,
			})
				.then((res) => res.json())
				.then((data) => setError(data.error || null))
				.catch((err) => setError(err.message || 'Unknown error'));
		}, [blockLogic]);


		if (error) {
			wrapperProps = assign(wrapperProps, {
				'data-spk-block-logic-error': __('Block Logic Error:', 'block-logic') + ' ' + error,
			});
		}

		return <BlockListBlock {...props} wrapperProps={wrapperProps} />;
	};
}, 'addWrapperDataAttribute');

addFilter(
	'editor.BlockListBlock',
	'blockLogic/addWrapperDataAttribute',
	addWrapperDataAttribute,
);
